import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../shared/material.module';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { Helpers } from '../../../utilities/helpers';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { UpsertMultiYearPyramidRequest, YearPercentage } from '../../../models/api/upsertMultiYearPyramidRequest';
import { HotToastService } from '@ngxpert/hot-toast';
import { MatDialogRef } from '@angular/material/dialog';
import { FormPyramidItemComponent } from '../form-pyramid-item/form-pyramid-item.component';
import { PyramidDto } from '../../../models/api/pyramidDto';
import { PyramidEntriesByYearDto } from '../../../models/api/pyramidEntriesByYearDto';

@Component({
  selector: 'app-edit-pyramid-item',
  imports: [
    CommonModule,
    NgxSpinnerModule,
    MaterialModule,
    FormPyramidItemComponent
  ],
  templateUrl: './edit-pyramid-item.component.html',
  styleUrl: './edit-pyramid-item.component.scss'
})
export class EditPyramidItemComponent implements OnInit {
  @ViewChild(FormPyramidItemComponent) formComponent!: FormPyramidItemComponent;

  @Input() pyramidItem!: PyramidDto;
  @Input() largeShareholderId!: string;
  @Input() pyramidEntriesByYear?: PyramidEntriesByYearDto;
  @Input() dialogRef?: MatDialogRef<any>;
  @Output() formValidityChanged = new EventEmitter<boolean>();

  measurements: string[] = [];
  prefilledPercentages?: Map<number, number>;

  private largeShareholderService = inject(LargeShareholderService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  constructor() {
    // Load years from measurements helper
    this.measurements = Helpers.measurements();
  }

  ngOnInit(): void {
    // Prepare prefilled percentages map by extracting all year entries for this entity
    this.prefilledPercentages = new Map();

    if (this.pyramidEntriesByYear) {
      // Iterate through all years in the data
      Object.keys(this.pyramidEntriesByYear).forEach(yearStr => {
        const year = parseInt(yearStr, 10);
        const pyramidsForYear = this.pyramidEntriesByYear![yearStr];

        // Find the entry that matches this pyramid entity
        const matchingEntry = pyramidsForYear.find(p => this.isPyramidMatch(p));

        if (matchingEntry && matchingEntry.percentage) {
          this.prefilledPercentages!.set(year, matchingEntry.percentage);
        }
      });
    } else {
      // Fallback: use the current pyramid item's data
      if (this.pyramidItem.fromDate && this.pyramidItem.percentage) {
        const year = new Date(this.pyramidItem.fromDate).getFullYear();
        this.prefilledPercentages.set(year, this.pyramidItem.percentage);
      }
    }
  }

  /**
   * Check if a pyramid entry matches the current entity being edited
   */
  private isPyramidMatch(pyramid: PyramidDto): boolean {
    // Check each possible entity type
    if (this.pyramidItem.pyramidLargeShareholderId &&
        pyramid.pyramidLargeShareholderId === this.pyramidItem.pyramidLargeShareholderId) {
      return true;
    }
    if (this.pyramidItem.pyramidalShareholderId &&
        pyramid.pyramidalShareholderId === this.pyramidItem.pyramidalShareholderId) {
      return true;
    }
    if (this.pyramidItem.companyId &&
        pyramid.companyId === this.pyramidItem.companyId) {
      return true;
    }
    if (this.pyramidItem.opbCompanyId &&
        pyramid.opbCompanyId === this.pyramidItem.opbCompanyId) {
      return true;
    }
    if (this.pyramidItem.directorId &&
        pyramid.directorId === this.pyramidItem.directorId) {
      return true;
    }
    return false;
  }

  get pyramidName(): string {
    return this.pyramidItem.pyramidLargeShareholderName
      || this.pyramidItem.pyramidalShareholderName
      || this.pyramidItem.companyName
      || this.pyramidItem.opbCompanyName
      || this.pyramidItem.directorName
      || '';
  }

  onFormValidityChanged(isValid: boolean): void {
    this.formValidityChanged.emit(isValid);
  }

  onConfirm(): void {
    if (!this.formComponent.isValid()) {
      this.toast.error('Please fill in all required fields');
      return;
    }

    const dialogObservable = this.confirmationDialogService.openUpdateConfirmationDialog();

    dialogObservable.subscribe((result: boolean) => {
      if (result) {
        this.submitPyramid();
      }
    });
  }

  private submitPyramid(): void {
    const formData = this.formComponent.getFormValue();

    // Extract year-percentage pairs from the form
    const yearPercentages: YearPercentage[] = [];
    this.measurements.forEach(measurement => {
      const percentage = formData[`directPercentage_${measurement}`];
      if (percentage !== null && percentage !== undefined && percentage !== '') {
        yearPercentages.push({
          year: parseInt(measurement, 10),
          percentage: parseFloat(percentage)
        });
      }
    });

    this.spinnerService.show();

    // Build the request object
    const request: UpsertMultiYearPyramidRequest = {
      largeShareholderId: this.largeShareholderId,
      categoryId: formData.category?.id || this.pyramidItem.categoryId,
      subCategoryId: formData.subcategory?.id || this.pyramidItem.subCategoryId,
      yearPercentages: yearPercentages
    };

    // Set the appropriate entity ID based on the pyramid item type
    if (this.pyramidItem.pyramidLargeShareholderId) {
      request.pyramidLargeShareholderId = this.pyramidItem.pyramidLargeShareholderId;
    } else if (this.pyramidItem.pyramidalShareholderId) {
      request.pyramidalShareholderId = this.pyramidItem.pyramidalShareholderId;
    } else if (this.pyramidItem.companyId) {
      request.companyId = this.pyramidItem.companyId;
    } else if (this.pyramidItem.opbCompanyId) {
      request.opbCompanyId = this.pyramidItem.opbCompanyId;
    } else if (this.pyramidItem.directorId) {
      request.directorId = this.pyramidItem.directorId;
    }

    this.largeShareholderService.upsertMultiYearPyramid(request).subscribe({
      next: (pyramids) => {
        this.spinnerService.hide();
        this.toast.success('Pyramid entry updated successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });

        // Close the dialog and return the result
        if (this.dialogRef) {
          this.dialogRef.close({ success: true, pyramids });
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error updating pyramid entry:', error);
        this.toast.error('Failed to update pyramid entry');
      }
    });
  }
}
